import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  input,
  model,
  output,
  signal,
  ViewEncapsulation,
  viewChild,
  booleanAttribute,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { FaIconComponent } from '../../atoms';
import { faChevronDown, faChevronUp, faCheck } from '@fortawesome/free-solid-svg-icons';

export interface SelectOption<T = unknown> {
  label: string;
  value: T;
  disabled?: boolean;
  divider?: boolean;
}

@Component({
  selector: 'orbit-select',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FaIconComponent],
  templateUrl: './select.component.html',
  styleUrl: './select.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectComponent<T = unknown> {
  readonly #defaultController = new FormControl<T | null>(null);

  //#region Inputs
  controller = input<FormControl<T | null>>(this.#defaultController);
  disabled = input<boolean, unknown>(false, { transform: booleanAttribute });
  name = input<string>('');
  options = input<SelectOption<T>[]>([]);
  placeholder = input<string>('Select an option');
  testId = input<string>();
  //#endregion

  //#region Models
  selectedValue = model<T | null>(null);
  //#endregion

  //#region Outputs
  selectionChange = output<SelectOption<T> | null>();
  opened = output<boolean>();
  closed = output<boolean>();
  //#endregion

  //#region Signals
  isOpen = signal<boolean>(false);
  focusedOptionIndex = signal<number>(-1);
  //#endregion

  //#region View Children
  selectButton = viewChild<ElementRef<HTMLButtonElement>>('selectButton');
  optionsList = viewChild<ElementRef<HTMLUListElement>>('optionsList');
  dropdown = viewChild<ElementRef<HTMLDivElement>>('dropdown');
  //#endregion

  //#region Icons
  readonly chevronDown = faChevronDown;
  readonly chevronUp = faChevronUp;
  readonly checkIcon = faCheck;
  //#endregion

  //#region Computed
  selectedOption = computed(() => {
    const value = this.selectedValue() ?? this.controller().value;
    return this.options().find((option) => option.value === value) || null;
  });

  displayText = computed(() => {
    const selected = this.selectedOption();
    return selected?.label || this.placeholder();
  });

  enabledOptions = computed(() => {
    return this.options().filter((option) => !option.disabled && !option.divider);
  });

  chevronIcon = computed(() => {
    return this.isOpen() ? this.chevronUp : this.chevronDown;
  });
  //#endregion

  private readonly isInitialized = signal(false);

  constructor() {
    // Initialize from controller when controller changes (but only from controller to signal, never back)
    effect(() => {
      const controller = this.controller();
      const controllerValue = controller.value;
      const currentSignalValue = this.selectedValue();
      const isInitialized = this.isInitialized();

      // Only sync from controller to signal on initial load OR when signal is null and controller has value
      if (!isInitialized || (currentSignalValue === null && controllerValue !== null)) {
        this.selectedValue.set(controllerValue);
        this.isInitialized.set(true);
      }
    });

    // Sync selectedValue with controller (signal -> controller only)
    effect(() => {
      const selectedValue = this.selectedValue();
      const isInitialized = this.isInitialized();

      if (
        isInitialized &&
        selectedValue !== undefined &&
        selectedValue !== this.controller().value
      ) {
        this.controller().setValue(selectedValue);
      }
    });

    // Handle disabled state
    effect(() => {
      if (this.disabled()) {
        this.controller().disable();
      } else {
        this.controller().enable();
      }
    });
  }

  //#region Event Handlers
  toggleDropdown(): void {
    if (this.disabled()) return;

    if (this.isOpen()) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown(): void {
    if (this.disabled()) return;

    this.isOpen.set(true);
    this.opened.emit(true);

    // Focus the current selected option or first option
    const selectedIndex = this.getSelectedOptionIndex();
    if (selectedIndex >= 0) {
      this.focusedOptionIndex.set(selectedIndex);
    } else {
      this.focusedOptionIndex.set(0);
    }

    // Position the dropdown after it's rendered
    setTimeout(() => this.positionDropdown(), 0);
  }

  closeDropdown(): void {
    this.isOpen.set(false);
    this.focusedOptionIndex.set(-1);
    this.closed.emit(false);

    // Return focus to the select button
    setTimeout(() => {
      this.selectButton()?.nativeElement?.focus();
    });
  }

  selectOption(option: SelectOption<T>): void {
    if (option.disabled || option.divider) return;

    this.selectedValue.set(option.value);
    this.selectionChange.emit(option);
    this.closeDropdown();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (this.disabled()) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.handleArrowDown();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.handleArrowUp();
        break;

      case 'Enter':
      case ' ':
        event.preventDefault();
        this.handleEnterOrSpace();
        break;

      case 'Escape':
        event.preventDefault();
        this.closeDropdown();
        break;

      case 'Tab':
        this.handleTab();
        break;
    }
  }

  private handleArrowDown(): void {
    if (!this.isOpen()) {
      this.openDropdown();
      return;
    }

    const enabledOptions = this.enabledOptions();
    const currentIndex = this.focusedOptionIndex();
    const nextIndex = currentIndex < enabledOptions.length - 1 ? currentIndex + 1 : 0;
    this.focusedOptionIndex.set(nextIndex);
  }

  private handleArrowUp(): void {
    if (!this.isOpen()) {
      this.openDropdown();
      return;
    }

    const enabledOptions = this.enabledOptions();
    const currentIndex = this.focusedOptionIndex();
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : enabledOptions.length - 1;
    this.focusedOptionIndex.set(prevIndex);
  }

  private handleEnterOrSpace(): void {
    if (!this.isOpen()) {
      this.openDropdown();
      return;
    }

    const enabledOptions = this.enabledOptions();
    const currentIndex = this.focusedOptionIndex();

    if (currentIndex >= 0 && currentIndex < enabledOptions.length) {
      const selectedOption = enabledOptions[currentIndex];
      if (selectedOption) {
        this.selectOption(selectedOption);
      }
    }
  }

  private handleTab(): void {
    if (this.isOpen()) {
      this.closeDropdown();
    }
  }

  onOptionClick(option: SelectOption<T>, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.selectOption(option);
  }

  onOptionMouseEnter(index: number): void {
    const option = this.options()[index];
    if (option && !option.disabled && !option.divider) {
      this.focusedOptionIndex.set(this.getEnabledOptionIndex(index));
    }
  }

  onOptionKeyDown(option: SelectOption<T>, event: KeyboardEvent): void {
    if (option.disabled || option.divider) return;

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        event.stopPropagation();
        this.selectOption(option);
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.closeDropdown();
        break;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as Element;
    const selectElement = this.selectButton()?.nativeElement;
    const optionsElement = this.optionsList()?.nativeElement;

    if (selectElement && optionsElement) {
      const isClickInsideSelect = selectElement.contains(target) || optionsElement.contains(target);

      if (!isClickInsideSelect && this.isOpen()) {
        this.closeDropdown();
      }
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.#repositionDropdownIfOpen();
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(): void {
    this.#repositionDropdownIfOpen();
  }
  //#endregion

  //#region Helper Methods
  #repositionDropdownIfOpen(): void {
    if (this.isOpen()) {
      this.positionDropdown();
    }
  }

  private getSelectedOptionIndex(): number {
    const selectedValue = this.selectedValue() ?? this.controller().value;
    const enabledOptions = this.enabledOptions();
    return enabledOptions.findIndex((option) => option.value === selectedValue);
  }

  getEnabledOptionIndex(optionIndex: number): number {
    const option = this.options()[optionIndex];
    if (!option) return -1;
    const enabledOptions = this.enabledOptions();
    return enabledOptions.findIndex((enabledOption) => enabledOption.value === option.value);
  }

  isOptionFocused(index: number): boolean {
    const enabledIndex = this.getEnabledOptionIndex(index);
    return enabledIndex === this.focusedOptionIndex();
  }

  isOptionSelected(option: SelectOption<T>): boolean {
    const currentValue = this.selectedValue() ?? this.controller()?.value;
    return option.value === currentValue;
  }

  private positionDropdown(): void {
    const buttonElement = this.selectButton()?.nativeElement;
    const dropdownElement = this.dropdown()?.nativeElement;

    if (!buttonElement || !dropdownElement) {
      return;
    }

    // Get button position
    const buttonRect = buttonElement.getBoundingClientRect();

    // Validate button position
    if (buttonRect.width === 0 || buttonRect.height === 0) {
      setTimeout(() => this.positionDropdown(), 10);
      return;
    }

    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Force dropdown to be positioned correctly
    dropdownElement.style.position = 'fixed';
    dropdownElement.style.zIndex = '1000';
    dropdownElement.style.visibility = 'hidden';
    dropdownElement.style.display = 'block';
    dropdownElement.style.top = `${buttonRect.bottom + 4}px`;
    dropdownElement.style.left = `${buttonRect.left}px`;

    // Get dropdown dimensions after positioning
    const dropdownRect = dropdownElement.getBoundingClientRect();

    // Calculate final position
    let top = buttonRect.bottom + 4; // 4px gap below the button
    let left = buttonRect.left;
    const width = Math.max(buttonRect.width, 200); // Minimum 200px width
    const dropdownHeight = Math.min(192, dropdownRect.height); // Max 12rem = 192px

    // Adjust if dropdown would go off-screen horizontally
    if (left + width > viewportWidth - 8) {
      left = Math.max(8, viewportWidth - width - 8); // 8px margin from edge
    }

    // Adjust if dropdown would go off-screen vertically
    if (top + dropdownHeight > viewportHeight - 8) {
      // Show above the button instead
      top = Math.max(8, buttonRect.top - dropdownHeight - 4);
    }

    // Apply final positioning and make visible
    dropdownElement.style.top = `${Math.round(top)}px`;
    dropdownElement.style.left = `${Math.round(left)}px`;
    dropdownElement.style.width = `${Math.round(width)}px`;
    dropdownElement.style.visibility = 'visible';
  }
  //#endregion
}
